import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router';
import { Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSortPreference } from 'hooks/useSortPreference';
import { useFetchPublicHaveListQuery } from 'store/services/collection/publicCollectionApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { MTGCard } from 'types/interfaces/mtgCard';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { SortOrder } from 'types/interfaces/sort';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import TextInputField from 'components/TextInputField';
import CardsTable from 'components/CardsTable';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { SetIcon } from 'components/SetIcon';
import { AssetUrls } from 'config/assets';
import { getCardImageUrl, SORT_OPTIONS, VIEW_OPTIONS } from 'utils/wantListHelpers';
import './ProfileHaveListTab.css';

interface ProfileHaveListTabProps {
  username: string;
  isOwnProfile?: boolean;
}

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Chevron SVG used in dropdowns
const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`profile-have-dropdown-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Condition abbreviation map
const CONDITION_MAP: Record<string, string> = {
  'NEAR_MINT': 'NM',
  'SLIGHTLY_PLAYED': 'LP',
  'MODERATELY_PLAYED': 'MP',
  'HEAVILY_PLAYED': 'HP',
  'DAMAGED': 'DMG',
};

// Card item component
const HaveListCardItem: React.FC<{ item: CollectionList }> = ({ item }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;
  const imageUrl = getCardImageUrl(item);

  const cardName = item.card?.name || 'Unknown Card';

  const handleClick = () => {
    if (item.card) {
      navigate(buildCardUrl(item.card));
    }
  };

  return (
    <div className="profile-have-card-item" onClick={handleClick}>
      <div className="profile-have-card-image-container">
        <img
          src={imageError ? defaultImage : imageUrl}
          alt={cardName}
          className="profile-have-card-image"
          onError={() => setImageError(true)}
          loading="lazy"
        />
        {item.quantity > 1 && (
          <div className="profile-have-card-quantity">x{item.quantity}</div>
        )}
        {item.foil && <div className="profile-have-card-foil">FOIL</div>}
      </div>
      <div className="profile-have-card-info">
        <span className="profile-have-card-name" title={cardName}>
          {cardName}
        </span>
        {item.displayPrice != null && (
          <Points points={item.displayPrice} type="small" />
        )}
      </div>
    </div>
  );
};

// Loading skeleton
const HaveListSkeleton: React.FC = () => (
  <div className="profile-have-skeleton">
    <div className="profile-have-header">
      <Skeleton variant="text" width={120} height={28} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
    </div>
    <div className="profile-have-grid">
      {[...Array(12)].map((_, i) => (
        <div key={i} className="profile-have-card-item">
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              aspectRatio: '488 / 680',
              borderRadius: '8px',
            }}
          />
          <Skeleton variant="text" width="80%" height={16} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        </div>
      ))}
    </div>
  </div>
);

// Private have list message
interface PrivateHaveListMessageProps {
  isOwnProfile?: boolean;
}

const PrivateHaveListMessage: React.FC<PrivateHaveListMessageProps> = ({ isOwnProfile }) => (
  <div className="profile-have-private">
    <div className="profile-have-private-icon">&#128274;</div>
    <h3 className="profile-have-private-title">Have List is Private</h3>
    {isOwnProfile ? (
      <>
        <p className="profile-have-private-description">
          Your have list is currently set to private.
        </p>
        <Link to="/settings" className="profile-have-private-settings-link">
          Update Privacy in Settings
        </Link>
      </>
    ) : (
      <p className="profile-have-private-description">
        This user has chosen to keep their have list private.
      </p>
    )}
  </div>
);

export const ProfileHaveListTab: React.FC<ProfileHaveListTabProps> = ({ username, isOwnProfile }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Sort state - persisted to URL params and localStorage
  const { orderBy: sortOrder, setOrderBy: setSortOrder } = useSortPreference('profileHaveListSort', 'created_at', 'desc');

  // View mode state - persist to sessionStorage
  const [viewMode, setViewMode] = useState<'images' | 'list'>(() => {
    const saved = sessionStorage.getItem('profileHaveListViewMode');
    return (saved as 'images' | 'list') || 'images';
  });

  // Image size state - persist to localStorage
  type ImageSize = 'S' | 'M' | 'L';
  const [imageSize, setImageSize] = useState<ImageSize>(() => {
    const saved = localStorage.getItem('profileHaveListImageSize');
    return (saved as ImageSize) || 'S';
  });

  const handleImageSizeChange = useCallback((size: ImageSize) => {
    setImageSize(size);
    localStorage.setItem('profileHaveListImageSize', size);
  }, []);

  // Dropdown states
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const sortRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Synchronous pagination reset during render (prevents stale data flash)
  const prevFilterDepsRef = useRef({
    search: '',
    sort: 'created_at' as string | undefined,
    direction: 'desc' as string | undefined,
  });

  const filterDepsChanged =
    prevFilterDepsRef.current.search !== debouncedSearchTerm ||
    prevFilterDepsRef.current.sort !== sortOrder.sort ||
    prevFilterDepsRef.current.direction !== sortOrder.direction;

  if (filterDepsChanged) {
    setPage(1);
    setPreviousPages([]);
    prevFilterDepsRef.current = {
      search: debouncedSearchTerm,
      sort: sortOrder.sort,
      direction: sortOrder.direction,
    };
  }

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
      if (viewRef.current && !viewRef.current.contains(event.target as Node)) {
        setIsViewOpen(false);
      }
    };
    if (isSortOpen || isViewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen, isViewOpen]);

  // Sort handlers
  const currentSortValue = `${sortOrder.sort}-${sortOrder.direction}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sort, direction] = compoundValue.split('-');
    setSortOrder({ sort, direction: direction as SortOrder });
    setIsSortOpen(false);
  }, [setSortOrder]);

  // View mode handler
  const handleViewModeChange = useCallback((mode: 'images' | 'list') => {
    setViewMode(mode);
    sessionStorage.setItem('profileHaveListViewMode', mode);
    setIsViewOpen(false);
  }, []);

  const { data, isLoading, isFetching, error } = useFetchPublicHaveListQuery({
    username,
    page,
    limit: 48,
    sort: sortOrder.sort,
    direction: sortOrder.direction,
    search: debouncedSearchTerm || undefined,
  });

  // Check if it's a 403 forbidden error (private have list)
  const isPrivate = error && typeof error === 'object' && 'status' in error && error.status === 403;

  // When loading more pages, accumulate previous results
  const handleLoadMore = () => {
    if (data?.haveList) {
      setPreviousPages((prev) => [...prev, ...data.haveList]);
    }
    setPage((p) => p + 1);
  };

  // Reset when username changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [username]);

  // Handle private have list
  if (isPrivate) {
    return <PrivateHaveListMessage isOwnProfile={isOwnProfile} />;
  }

  if (isLoading) {
    return <HaveListSkeleton />;
  }

  // Combine previous pages with current page data
  const allItems = [...previousPages, ...(data?.haveList || [])];

  // Track debounce pending state
  const isSearchPending = searchTerm !== debouncedSearchTerm;

  const totalCards = data?.totalCards || 0;
  const totalValue = data?.totalValue || 0;
  const hasMore = data?.hasMore || false;

  // Generate table cells for list view
  const tableCellsData: CardsTableCellsData = (() => {
    if (viewMode !== 'list') return { headCells: [], bodyRows: [] };

    const headCells: { key: string; label: string; columnWidth?: number; noSort?: boolean }[] = [
      { key: 'card_name', label: 'Name' },
      { key: 'setName', label: 'Set', columnWidth: 80, noSort: true },
    ];

    if (!isMobile) {
      headCells.push(
        { key: 'condition', label: 'Cond', columnWidth: 60, noSort: true },
        { key: 'isFoil', label: 'Foil', columnWidth: 50, noSort: true },
        { key: 'reference_price', label: 'Price', columnWidth: 80, noSort: true },
      );
    }

    headCells.push({ key: 'quantity', label: 'Qty', columnWidth: 50, noSort: true });

    return {
      headCells,
      bodyRows: allItems.map((row) => {
        const components: React.ReactNode[] = [
          // Name with thumbnail
          <div className="collection-card-name" key="name">
            {row.card?.scryfallID && (
              <img
                src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
                className="collection-card-name-img"
                alt={row.cardName || row.card?.name || 'Card'}
                onError={(e) => {
                  const img = e.currentTarget;
                  const scryfallId = row.card?.scryfallID;
                  if (scryfallId && !img.dataset.formatFallbackComplete) {
                    const currentSrc = img.src;
                    if (currentSrc.includes('/art_crop/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'normal');
                      return;
                    } else if (currentSrc.includes('/normal/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                      return;
                    } else if (currentSrc.includes('/small/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                      return;
                    }
                    img.dataset.formatFallbackComplete = 'true';
                  }
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
            <span className="collection-card-name-text">
              {row.cardName || row.card?.name}
            </span>
          </div>,
          // Set with icon + code
          <div className="collection-card-set" key="set">
            {row.card?.setIconUrl ? (
              <>
                <SetIcon
                  setIconUrl={row.card.setIconUrl}
                  setName={row.card?.setName || ''}
                  inverted={false}
                  size="sm"
                />
                <span className="collection-card-set-code">
                  {(row.card?.setCode || '').toUpperCase()}
                </span>
              </>
            ) : (
              <span className="collection-card-set-code">
                {(row.card?.setCode || '').toUpperCase()}
              </span>
            )}
          </div>,
        ];

        if (!isMobile) {
          components.push(
            // Condition
            <div key="cond">{row.condition ? (CONDITION_MAP[row.condition] || row.condition) : '-'}</div>,
            // Foil
            <div key="foil">
              {row.foil ? (
                <img
                  src={`${AssetUrls.base}/assets/add-cards/foil.png`}
                  width={14.15}
                  height={14.21}
                  alt="foil-icon"
                />
              ) : '-'}
            </div>,
            // Price
            <div key="price">
              {row.displayPrice != null
                ? <Points points={row.displayPrice} type="small" />
                : '???'}
            </div>,
          );
        }

        // Quantity
        components.push(
          <div key="qty"><span>{row.quantity}</span></div>,
        );

        return {
          key: row.id.toString(),
          card: row.card || ({} as MTGCard),
          components,
          onClick: () => {
            if (row.card) {
              navigate(buildCardUrl(row.card));
            }
          },
        };
      }),
    };
  })();

  // Controls bar
  const renderControls = () => (
    <div className="profile-have-controls">
      <div className="profile-have-search">
        <TextInputField
          leftComponent={<FontAwesomeIcon icon="magnifying-glass" color="#DD5BEA" size="sm" />}
          value={searchTerm}
          placeholder="Card Name"
          onChange={setSearchTerm}
        />
      </div>

      {/* View mode dropdown (desktop) */}
      <div className="profile-have-view-selector profile-have-desktop-only" ref={viewRef}>
        <span className="profile-have-dropdown-label">View</span>
        <button
          type="button"
          className={`profile-have-dropdown-trigger ${isViewOpen ? 'open' : ''}`}
          onClick={() => setIsViewOpen(!isViewOpen)}
          aria-haspopup="listbox"
          aria-expanded={isViewOpen}
          aria-label="Select view mode"
        >
          <img
            src={VIEW_OPTIONS.find(o => o.value === viewMode)?.icon}
            alt=""
            className="profile-have-dropdown-icon"
          />
          <span className="profile-have-dropdown-text">
            {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
          </span>
          <ChevronSvg open={isViewOpen} />
        </button>
        {isViewOpen && (
          <div className="profile-have-dropdown-menu" role="listbox">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`profile-have-dropdown-option ${viewMode === option.value ? 'selected' : ''}`}
                onClick={() => handleViewModeChange(option.value)}
                role="option"
                aria-selected={viewMode === option.value}
              >
                <img src={option.icon} alt="" className="profile-have-dropdown-option-icon" />
                <span>{option.label}</span>
              </button>
            ))}
            {viewMode === 'images' && (
              <>
                <div className="profile-have-dropdown-divider" />
                <div className="profile-have-size-toggle">
                  <span className="profile-have-size-label">Size</span>
                  <div className="profile-have-size-options">
                    {(['S', 'M', 'L'] as const).map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={`profile-have-size-btn ${imageSize === size ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleImageSizeChange(size); }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Sort dropdown (desktop) */}
      <div className="profile-have-sort-selector profile-have-desktop-only" ref={sortRef}>
        <span className="profile-have-dropdown-label">Sort</span>
        <button
          type="button"
          className={`profile-have-dropdown-trigger ${isSortOpen ? 'open' : ''}`}
          onClick={() => setIsSortOpen(!isSortOpen)}
          aria-haspopup="listbox"
          aria-expanded={isSortOpen}
          aria-label="Select sort order"
        >
          <img src={AssetUrls.icons.controls.sort} alt="" className="profile-have-dropdown-icon" />
          <span className="profile-have-dropdown-text">{currentSortLabel}</span>
          <ChevronSvg open={isSortOpen} />
        </button>
        {isSortOpen && (
          <div className="profile-have-dropdown-menu" role="listbox">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`profile-have-dropdown-option ${currentSortValue === option.value ? 'selected' : ''}`}
                onClick={() => handleSortSelect(option.value)}
                role="option"
                aria-selected={currentSortValue === option.value}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );

  // Determine empty states
  const isEmpty = allItems.length === 0;
  const showSearchEmptyState = isEmpty && debouncedSearchTerm && !isSearchPending && !isFetching;
  const showGenericEmptyState = isEmpty && !debouncedSearchTerm && !isSearchPending && !isFetching;
  const showLoadingState = isEmpty && (isSearchPending || isFetching);

  return (
    <div className="profile-have-tab">
      <div className="profile-have-header">
        <span className="profile-have-count">{formatNumber(totalCards)} Cards</span>
        {totalValue > 0 && <Points points={totalValue} />}
      </div>

      {renderControls()}

      {showLoadingState && (
        <div className={`profile-have-grid size-${imageSize.toLowerCase()}`}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className="profile-have-card-item">
              <Skeleton
                variant="rectangular"
                width="100%"
                sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
              />
            </div>
          ))}
        </div>
      )}

      {showSearchEmptyState && (
        <div className="profile-have-no-results">
          <p>No cards matching &ldquo;{debouncedSearchTerm}&rdquo;</p>
        </div>
      )}

      {showGenericEmptyState && (
        <div className="profile-have-empty">
          <p>This user&apos;s have list is empty.</p>
        </div>
      )}

      {!isEmpty && viewMode === 'images' && (
        <div className={`profile-have-grid size-${imageSize.toLowerCase()}`}>
          {allItems.map((item) => (
            <HaveListCardItem key={item.id} item={item} />
          ))}
        </div>
      )}

      {!isEmpty && viewMode === 'list' && (
        <div className="profile-have-list-view">
          <CardsTable
            isLoading={false}
            cellsData={tableCellsData}
          />
        </div>
      )}

      {hasMore && (
        <div className="profile-have-load-more">
          <MagicButton
            label={isFetching ? 'Loading...' : 'Load More'}
            color="dark-grey"
            onPress={handleLoadMore}
            isLoading={isFetching}
            isDisabled={isFetching}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileHaveListTab;
